import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { T, FONT_STACK } from '../customer/customers.theme';
import { Spinner } from '../customer/CustomersUI';
import ConfirmDialog from '../admin/ConfirmDialog';
import QrCanvas from './QrCanvas';
import downloadQrPng from './downloadQrPng';
import { COPY, CONFIRM_OFF, menuLinkFor } from './publicMenu.copy';
import { setPublicMenu, classifyToggleError } from './publicMenuApi';
import { setBusiness, setActiveUser } from '../../SignupAndLogin/loginSlice';

/**
 * Administration → Public menu.
 *
 * Three things on one screen: whether the menu is public, the code that opens
 * it, and a way to get that code onto the tables.
 *
 * The code is drawn whenever the shop has a `publicBusinessId`, even when the
 * menu is switched off — the address never changes, so a manager can download
 * the image today and switch the menu on the day the tables are laid. What
 * changes when it is off is the wording, which says plainly that a scan will
 * not work yet.
 */
export default function PublicMenuPanel({ onNotice }) {
  const dispatch = useDispatch();

  const activeUser = useSelector((state) => state.login.activeUser);
  const token = useSelector((state) => state.login.token);
  const business = useSelector((state) => state.login.business) || activeUser?.business;

  const publicId = business?.publicBusinessId || '';
  const isOn = Boolean(business?.publicMenu);
  const link = menuLinkFor(publicId);

  const [busy, setBusy] = React.useState(false);
  const [confirmingOff, setConfirmingOff] = React.useState(false);
  const [refused, setRefused] = React.useState(false);

  const say = React.useCallback(
    (message, isError) => {
      if (onNotice) onNotice(message, isError);
    },
    [onNotice]
  );

  /* ── switching it on and off ──────────────────────────────────────── */

  const apply = async (next) => {
    if (busy || !business?._id) return;
    setBusy(true);
    setRefused(false);
    try {
      // No id: the server takes the business from the token.
      const updated = await setPublicMenu({ on: next, token });

      // Both copies move, the way the rest of the app reads the business.
      const merged = updated || { ...business, publicMenu: next };
      dispatch(setBusiness(merged));
      if (activeUser) dispatch(setActiveUser({ ...activeUser, business: merged }));

      say(next ? COPY.savedOn : COPY.savedOff, false);
    } catch (error) {
      if (classifyToggleError(error) === 'refused') {
        setRefused(true);
        say(COPY.refused, true);
      } else {
        say(COPY.failed, true);
      }
    } finally {
      setBusy(false);
      setConfirmingOff(false);
    }
  };

  /**
   * The code is drawn into a PNG and handed straight to the browser — no
   * preview window and no print dialog in the way. It is encoded again at
   * printing resolution rather than saving the small one on screen; see
   * downloadQrPng.js for why that matters.
   */
  const saveImage = () => {
    const ok = downloadQrPng({
      value: link,
      businessName: business?.businessName || '',
    });
    say(ok ? COPY.downloaded : COPY.downloadFailed, !ok);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      say(COPY.copied, false);
    } catch (err) {
      say(COPY.copyFailed, true);
    }
  };

  /* ── chrome ───────────────────────────────────────────────────────── */

  const Card = ({ children, style }) => (
    <div
      style={{
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusPanel,
        padding: 22,
        ...style,
      }}
    >
      {children}
    </div>
  );

  const button = (kind) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: 40,
    padding: '0 18px',
    border: kind === 'ghost' ? `1px solid ${T.borderStrong}` : 'none',
    borderRadius: T.radiusControl,
    background:
      kind === 'primary' ? T.brand : kind === 'danger' ? T.dangerTint : T.surface,
    color: kind === 'primary' ? T.surface : kind === 'danger' ? T.danger : T.ink,
    font: 'inherit',
    fontFamily: FONT_STACK,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: busy ? 'progress' : 'pointer',
    opacity: busy ? 0.7 : 1,
    whiteSpace: 'nowrap',
  });

  /* ── never switched on: there is no address yet ───────────────────── */

  if (!publicId) {
    return (
      <div style={{ maxWidth: 820, fontFamily: FONT_STACK }}>
        <Card>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
            {COPY.notReadyTitle}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.65, color: T.inkSoft, maxWidth: 560 }}>
            {COPY.notReadyBody}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 820, fontFamily: FONT_STACK }}>
      <div style={{ fontSize: 13, lineHeight: 1.65, color: T.inkSoft, marginBottom: 18, maxWidth: 620 }}>
        {COPY.intro}
      </div>

      {/* ── on or off ──────────────────────────────────────────────── */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <span
            style={{
              flex: 'none',
              width: 10,
              height: 10,
              marginTop: 5,
              borderRadius: T.radiusPill,
              background: isOn ? T.paid : T.inkGhost,
            }}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              {isOn ? COPY.statusOn : COPY.statusOff}
            </div>
            <div
              style={{
                fontSize: 12.5,
                lineHeight: 1.6,
                color: T.inkSoft,
                marginTop: 4,
                maxWidth: 480,
              }}
            >
              {isOn ? COPY.statusOnBody : COPY.statusOffBody}
            </div>
          </div>

          <button
            type="button"
            disabled={busy}
            onClick={() => (isOn ? setConfirmingOff(true) : apply(true))}
            style={{ ...button(isOn ? 'ghost' : 'primary'), marginLeft: 'auto', flex: 'none' }}
          >
            {busy ? <Spinner /> : null}
            {busy ? COPY.working : isOn ? COPY.turnOff : COPY.turnOn}
          </button>
        </div>

        {refused ? (
          <div
            style={{
              marginTop: 16,
              padding: '12px 14px',
              background: T.pendingTint,
              border: `1px solid ${T.pendingBorder}`,
              borderRadius: T.radiusControl,
            }}
          >
            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#7A4E06' }}>
              {COPY.refusedTitle}
            </div>
            <div style={{ fontSize: 12.5, lineHeight: 1.6, color: '#7A4E06', marginTop: 3 }}>
              {COPY.refused}
            </div>
          </div>
        ) : null}
      </Card>

      {/* ── the code ───────────────────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', gap: 26, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: 'none' }}>
            {/* White plate behind it: a code on a grey page has a grey quiet
                zone, and that is exactly what a camera needs to be white. */}
            <div
              style={{
                padding: 10,
                background: '#FFFFFF',
                border: `1px solid ${T.border}`,
                borderRadius: T.radiusPanel,
                display: 'inline-block',
                lineHeight: 0,
              }}
            >
              <QrCanvas value={link} targetSize={240} />
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{COPY.qrTitle}</div>
            <div style={{ fontSize: 12.5, color: T.inkSoft, marginTop: 4 }}>{COPY.qrHint}</div>

            <div
              style={{
                marginTop: 16,
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                color: T.inkFaint,
              }}
            >
              {COPY.linkLabel}
            </div>
            <div
              style={{
                marginTop: 5,
                padding: '9px 12px',
                background: T.sunk,
                border: `1px solid ${T.border}`,
                borderRadius: T.radiusControl,
                fontSize: 12.5,
                color: T.ink,
                wordBreak: 'break-all',
                userSelect: 'all',
              }}
            >
              {link}
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
              <button type="button" onClick={saveImage} style={button('primary')}>
                {COPY.download}
              </button>

              <button type="button" onClick={copyLink} style={button('ghost')}>
                {COPY.copyLink}
              </button>
            </div>
          </div>
        </div>
      </Card>

      {confirmingOff ? (
        <ConfirmDialog
          title={CONFIRM_OFF.title}
          confirmLabel={CONFIRM_OFF.confirmLabel}
          body={CONFIRM_OFF.body}
          note={CONFIRM_OFF.note}
          onClose={() => setConfirmingOff(false)}
          onConfirm={() => apply(false)}
        />
      ) : null}
    </div>
  );
}
